import React, { forwardRef } from 'react'

import { twMerge } from 'tailwind-merge'

import {
  Tooltip,
  TooltipContent,
  type TooltipContentProps,
  type TooltipProps,
  TooltipTrigger,
  type TooltipTriggerProps,
} from '@/components/common/TooltipV3'

export type IconButtonVariant =
  | 'soft'
  | 'danger'
  | 'blue'
  | 'violet'
  | 'accent'
  | 'positive'

export interface IconButtonProps extends Omit<
  React.HTMLProps<HTMLElement>,
  'as' | 'type'
> {
  as?: React.ElementType
  type?: 'button' | 'submit' | 'reset'
  tooltip?: React.ReactNode
  tooltipTriggerProps?: TooltipTriggerProps
  tooltipProps?: TooltipProps
  tooltipContentProps?: TooltipContentProps
  variant?: IconButtonVariant
  isActive?: boolean
  disabled?: boolean
}

// Explicit transition properties: the bare `transition` shorthand also
// animates filter/backdrop-filter, which makes any blur/filter utility on a
// consumer animate on hover (fuzzy, repaint-heavy).
export const ICON_BUTTON_STYLES =
  'rounded-md p-1 transition-[background-color,border-color,color,opacity,transform,box-shadow] duration-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-400'

const VARIANT_STATIC_STYLES = {
  soft: 'text-primary-600 dark:text-primary-400',
  danger: 'text-red-600 dark:text-red-500',
  blue: 'text-blue-600 dark:text-blue-500',
  violet: 'text-violet-600 dark:text-violet-500',
  accent: 'bg-accent-400 dark:bg-accent-400 text-white dark:text-white',
  positive: 'text-green-600 dark:text-green-500',
}

const VARIANT_HOVER_STYLES = {
  soft: 'hover:bg-primary-100 dark:hover:bg-primary-700',
  danger: 'hover:bg-red-100 dark:hover:bg-red-600/50',
  blue: 'hover:bg-blue-100 dark:hover:bg-blue-600/50',
  violet: 'hover:bg-violet-100 dark:hover:bg-violet-600/50',
  accent: 'hover:opacity-70',
  positive: 'hover:bg-green-100 dark:hover:bg-green-600/50',
}

const VARIANT_ACTIVE_STYLES = {
  soft: 'bg-primary-200 dark:bg-primary-800',
  danger: 'bg-red-200 dark:bg-red-600/50',
  blue: 'bg-blue-200 dark:bg-blue-600/50',
  violet: 'bg-violet-200 dark:bg-violet-600/50',
  accent: 'bg-accent-400 dark:bg-accent-400',
  positive: 'bg-green-200 dark:bg-green-600/50',
}

export const BaseIconButton = forwardRef<HTMLElement, IconButtonProps>(
  (
    {
      as = 'button',
      children,
      className,
      disabled,
      variant = 'soft',
      isActive,
      type,
      ...props
    },
    ref,
  ) => {
    const Component = as
    const iconButtonClassName = twMerge(
      ICON_BUTTON_STYLES,
      VARIANT_STATIC_STYLES[variant],
      disabled
        ? 'opacity-50 cursor-not-allowed'
        : isActive
          ? VARIANT_ACTIVE_STYLES[variant]
          : VARIANT_HOVER_STYLES[variant],
      className ?? '',
    )

    if (Component === 'button') {
      return (
        <button
          ref={ref as React.Ref<HTMLButtonElement>}
          type={type ?? 'button'}
          disabled={disabled}
          className={iconButtonClassName}
          {...props}
        >
          {children}
        </button>
      )
    }

    if (Component === 'a') {
      const { href, onClick, tabIndex, ...anchorProps } = props
      const handleClick: React.MouseEventHandler<HTMLAnchorElement> = (
        event,
      ) => {
        if (disabled) {
          event.preventDefault()
          event.stopPropagation()
          return
        }

        onClick?.(event)
      }

      return (
        <a
          ref={ref as React.Ref<HTMLAnchorElement>}
          {...anchorProps}
          aria-disabled={disabled || anchorProps['aria-disabled']}
          className={iconButtonClassName}
          href={disabled ? undefined : href}
          onClick={handleClick}
          tabIndex={disabled ? -1 : tabIndex}
        >
          {children}
        </a>
      )
    }

    const { onClick, tabIndex, ...componentProps } = props
    const handleClick: React.MouseEventHandler<HTMLElement> = (event) => {
      if (disabled) {
        event.preventDefault()
        event.stopPropagation()
        return
      }

      onClick?.(event)
    }

    return (
      <Component
        ref={ref}
        {...componentProps}
        aria-disabled={disabled || componentProps['aria-disabled']}
        className={iconButtonClassName}
        onClick={handleClick}
        tabIndex={disabled ? -1 : tabIndex}
      >
        {children}
      </Component>
    )
  },
)

BaseIconButton.displayName = 'BaseIconButton'

const IconButtonV2 = forwardRef<HTMLElement, IconButtonProps>(
  (
    {
      children,
      tooltip,
      tooltipProps,
      tooltipContentProps,
      tooltipTriggerProps,
      ...props
    },
    ref,
  ) => {
    const { asChild, ...restTooltipTriggerProps } = tooltipTriggerProps ?? {}
    if (!tooltip)
      return (
        <BaseIconButton {...props} ref={ref}>
          {children}
        </BaseIconButton>
      )

    const button = (
      <BaseIconButton {...props} ref={ref}>
        {children}
      </BaseIconButton>
    )

    // Browsers don't fire pointer events on disabled <button>, so Radix's
    // Tooltip won't trigger on hover. Wrap in a span so the tooltip still
    // works while keeping the native `disabled` semantics on the button.
    // `inline-flex` keeps the wrapper hugging the button's bounding box so
    // the span doesn't inherit extra line-height and inflate layout.
    const trigger = props.disabled ? (
      <span className="inline-flex">{button}</span>
    ) : (
      button
    )

    return (
      <Tooltip
        {...tooltipProps}
        // TODO: figure out why setting this to 0 causes performance issues
        delayDuration={tooltipProps?.delayDuration ?? 100}
      >
        <TooltipTrigger asChild {...restTooltipTriggerProps}>
          {trigger}
        </TooltipTrigger>
        <TooltipContent {...tooltipContentProps}>{tooltip}</TooltipContent>
      </Tooltip>
    )
  },
)

IconButtonV2.displayName = 'IconButtonV2'
export default IconButtonV2
